import { toast } from 'react-toastify';

import { GetTransferableProductsQuery } from 'data/__generated__';
import { CsvDataRow } from 'components/TotemCsvInputButton';

type TransferableProduct = GetTransferableProductsQuery['transferableProducts'][number];

export type ProductDeltaRow = {
    productId: string;
    label: string;
    delta: number;
    conditionning: number;
    packagesPerBatch: number;
    originStockAvailable: number;
};

type HandleParams = {
    csvData: CsvDataRow[];
    transferableProducts: TransferableProduct[];
    deltaSign: 1 | -1;
    setProductRows: React.Dispatch<React.SetStateAction<ProductDeltaRow[]>>;
};

const toRow = (product: TransferableProduct, delta: number): ProductDeltaRow => ({
    productId: product.productId,
    label: product.fullname,
    delta,
    conditionning: product.conditionning,
    packagesPerBatch: product.packagesPerBatch,
    originStockAvailable: product.originStockAvailable,
});

export const handleMultiStockVariationCsvImport = ({
    csvData,
    transferableProducts,
    deltaSign,
    setProductRows,
}: HandleParams) => {
    try {
        const [header, ...rawData] = csvData;
        const data = rawData.filter((row) => row.some((cell) => cell.trim() !== ''));

        const productIdIndex = header.findIndex((elem) => elem === 'productId');
        const quantityIndex = header.findIndex((elem) => elem === 'quantity');
        const palletsIndex = header.findIndex((elem) => elem === 'pallets');
        const colisIndex = header.findIndex((elem) => elem === 'colis');

        if (!data.length) {
            toast.error('Veuillez vérifier le fichier CSV. Il semble être vide');
            return;
        }
        if (productIdIndex === -1 || (quantityIndex === -1 && palletsIndex === -1 && colisIndex === -1)) {
            toast.error(
                'Le fichier CSV doit contenir "productId" et au moins une colonne parmi: "quantity", "pallets", "colis"',
            );
            return;
        }

        const numberOfFields = header.length;
        const newRows: ProductDeltaRow[] = [];

        for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
            const row = data[rowIndex];
            if (row.length !== numberOfFields) {
                toast.error(`Mauvais nombre de colonnes sur la ligne ${rowIndex + 2}`);
                return;
            }

            const productId = row[productIdIndex];
            if (!productId) {
                toast.error(`ProductId manquant sur la ligne ${rowIndex + 2}`);
                return;
            }
            if (newRows.find((p) => p.productId === productId)) {
                toast.error(`ProductId ${productId} dupliqué sur la ligne ${rowIndex + 2}`);
                return;
            }

            const product = transferableProducts.find((p) => p.productId === productId);
            if (!product) {
                toast.error(`Produit non trouvé avec l'ID ${productId} sur la ligne ${rowIndex + 2}`);
                continue;
            }

            let unsignedAbs: number | null = null;

            if (quantityIndex !== -1 && row[quantityIndex]) {
                const quantity = Number(row[quantityIndex].replace(/\s+/g, ''));
                if (Number.isNaN(quantity) || !Number.isInteger(quantity)) {
                    toast.error(`Quantité non valide sur la ligne ${rowIndex + 2}`);
                    return;
                }
                unsignedAbs = Math.abs(quantity);
            }

            if (unsignedAbs === null && palletsIndex !== -1 && row[palletsIndex] && product.packagesPerBatch > 1) {
                const pallets = Number(row[palletsIndex].replace(/\s+/g, ''));
                if (Number.isNaN(pallets) || pallets < 0) {
                    toast.error(`Nombre de palettes non valide sur la ligne ${rowIndex + 2}`);
                    return;
                }
                unsignedAbs = Math.round(pallets * product.conditionning * product.packagesPerBatch);
            }

            if (unsignedAbs === null && colisIndex !== -1 && row[colisIndex] && product.conditionning > 1) {
                const colis = Number(row[colisIndex].replace(/\s+/g, ''));
                if (Number.isNaN(colis) || colis < 0) {
                    toast.error(`Nombre de colis non valide sur la ligne ${rowIndex + 2}`);
                    return;
                }
                unsignedAbs = Math.round(colis * product.conditionning);
            }

            if (unsignedAbs === null) {
                toast.error(`Aucune quantité valide sur la ligne ${rowIndex + 2}`);
                return;
            }

            newRows.push(toRow(product, unsignedAbs * deltaSign));
        }

        if (!newRows.length) {
            toast.error('Aucun produit valide trouvé dans le fichier CSV');
            return;
        }

        setProductRows((prev) => {
            const merged = [...prev];
            newRows.forEach((row) => {
                const i = merged.findIndex((p) => p.productId === row.productId);
                if (i !== -1) {
                    merged[i] = { ...merged[i], delta: row.delta };
                } else {
                    merged.push(row);
                }
            });
            return merged;
        });

        toast.success(`Import réussi de ${newRows.length} produits`);
    } catch {
        toast.error("Erreur lors de l'import du fichier CSV");
    }
};
